import { tracked } from '@glimmer/tracking';
import Constants from '../../utils/constants';
import cloneDeep from 'lodash.clonedeep';
import { TrackedObject, TrackedArray } from '@ember-compat/tracked-built-ins';

export default class TopologyInput {
  config = {};

  @tracked default = null;
  @tracked isDisabled = null;
  @tracked selectedOption = null;
  @tracked options = [];

  constructor(config) {
    this.config = config;
  }

  get id() {
    return this.config ? this.config.id : null;
  }

  get selectableOptions() {
    return (this.options || []).filter((option) => !(option.isDisabled || option.isHidden));
  }

  get shownOptions() {
    return (this.options || []).filter((option) => !option.isHidden);
  }

  get useInUrl() {
    return !this.isDisabled && this.config.isShown;
  }

  get isInverting() {
    return this.config &&
      (this.config.id === Constants.TOPOLOGY_INPUTS.INVERTING ||
        this.config.id === Constants.TOPOLOGY_INPUTS.MULTIPLYING_DAC)
      ? true
      : false;
  }

  get isSelectedOptionInverting() {
    if (!this.selectedOption) return false;
    const invertingTopologyOptions = [Constants.TOPOLOGY_OPTIONS.BUFFER, Constants.TOPOLOGY_OPTIONS.NO_BUFFER];
    return (
      this.selectedOption.type === Constants.TOPOLOGY_OPTIONS.INVERTING ||
      (this.config &&
        this.config.id === Constants.TOPOLOGY_INPUTS.INVERTING &&
        invertingTopologyOptions.includes(this.selectedOption.type))
    );
  }

  get isNonInverting() {
    return this.config && this.config.id === Constants.TOPOLOGY_INPUTS.NON_INVERTING;
  }

  get isSelectedOptionNonInverting() {
    if (!this.selectedOption) return false;
    const nonInvertingTopologyOptions = [
      Constants.TOPOLOGY_OPTIONS.NON_INVERTING_BUFFER,
      Constants.TOPOLOGY_OPTIONS.NON_INVERTING_NO_BUFFER,
    ];
    return nonInvertingTopologyOptions.includes(this.selectedOption.type);
  }

  get isSelectedOptionBuffered() {
    if (!this.selectedOption) return false;
    const bufferedTopologyOptions = [
      Constants.TOPOLOGY_OPTIONS.BUFFER,
      Constants.TOPOLOGY_OPTIONS.NON_INVERTING_BUFFER,
      Constants.TOPOLOGY_OPTIONS.INVERTING_BUFFER,
    ];
    return bufferedTopologyOptions.includes(this.selectedOption.type);
  }

  get isSelectedOptionNone() {
    if (!this.selectedOption) return false;
    return this.selectedOption.type === Constants.TOPOLOGY_OPTIONS.NONE;
  }

  get isSelectedOptionInternal() {
    if (!this.selectedOption) return false;
    return this.selectedOption.type === Constants.TOPOLOGY_OPTIONS.INTERNAL;
  }

  get isSelectedOptionExternal() {
    if (!this.selectedOption) return false;
    return this.selectedOption.type === Constants.TOPOLOGY_OPTIONS.EXTERNAL;
  }

  get selectedOptionHasExternalResistors() {
    if (!this.selectedOption) return false;
    return this.selectedOption.type === Constants.TOPOLOGY_OPTIONS.EXTERNAL_RESISTORS;
  }

  resetDefault(params) {
    this.default = this.config.defaultFn ? this.config.defaultFn(params) : this.config.default;
  }

  resetSelectableOptions(params) {
    const optionsFound = this.options && this.options.length;
    if (optionsFound) {
      this.options.forEach((option) => {
        option.isDisabled = option.disabledFn ? option.disabledFn(params) : false;
        option.isHidden = option.hiddenFn ? option.hiddenFn(params) : false;
      });
    }
  }

  resetIsDisabled(disabledParams) {
    const disable = this.config && this.config.disabledFn ? this.config.disabledFn(disabledParams) : false;
    if (disable) this.disable();
    else this.enable();
  }

  enable() {
    if (this.isDisabled) {
      this.isDisabled = false;
      this.resetSelectedOption();
    }
  }

  disable() {
    if (!this.isDisabled) {
      this.isDisabled = true;
      this.resetSelectedOptionForDisabledInput();
    }
  }

  resetSelectedOption() {
    const optionsFound = this.selectableOptions && this.selectableOptions.length;

    if (optionsFound && this.default) {
      // If default is not selectable, the first selectable option will be selected
      const selectedOption = this.selectableOptions.find((option) => option.id === this.default);
      this.selectedOption = selectedOption || this.selectableOptions[0];
    } else if (optionsFound) {
      this.selectedOption = this.selectableOptions[0];
    }
  }

  resetSelectedOptionForDisabledInput() {
    let selected = null;
    const optionsFound = this.selectableOptions && this.selectableOptions.length;

    if (optionsFound && this.config.disabledValue) {
      selected = this.selectableOptions.find((option) => option.id === this.config.disabledValue);
    }

    if (selected) {
      this.selectedOption = selected;
    }
  }

  resetOptions(data) {
    let options = [];
    this.config.options.forEach((option) => {
      if (
        option.associatedData &&
        option.associatedData.optionsLocation &&
        option.associatedData.optionsLocation in data
      ) {
        const associatedData = data[option.associatedData.optionsLocation];
        if (associatedData && Array.isArray(associatedData) && associatedData.length) {
          associatedData.forEach((item) => {
            let newOption = cloneDeep(option);
            newOption.id = `${option.id}-${item.value}`;
            newOption.associatedData.item = item;
            options.push(new TrackedObject(newOption));
          });
        } else options.push(new TrackedObject(cloneDeep(option)));
      } else options.push(new TrackedObject(cloneDeep(option)));
    });

    this.options = new TrackedArray(options);
  }
}
